import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { preferencesApi, moodsApi, servicesApi } from '@/lib/api';
import { Settings, Mail, Sparkles, Loader2, Eye, ShieldCheck, Crown, ArrowRight, HardDrive, Inbox, Cloud, Link2, Unlink, KeyRound } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function TwoFactorSection({ token }) {
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/auth/2fa/status`, { headers }).then(r => setStatus(r.data)).catch(() => {});
  }, []);

  const handleSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/setup`, {}, { headers });
      setSetupData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!code || code.length !== 6) { toast.error('Enter a 6-digit code'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/verify`, { code }, { headers });
      setRecoveryCodes(res.data.recovery_codes);
      setStatus({ enabled: true });
      setSetupData(null);
      setCode('');
      toast.success('2FA enabled!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!code || code.length !== 6) { toast.error('Enter a 6-digit code to disable'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { code }, { headers });
      setStatus({ enabled: false });
      setCode('');
      setRecoveryCodes(null);
      toast.success('2FA disabled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCodes = () => {
    if (recoveryCodes) {
      navigator.clipboard.writeText(recoveryCodes.join('\n'));
      toast.success('Recovery codes copied to clipboard');
    }
  };

  const handleDownloadCodes = () => {
    if (recoveryCodes) {
      const text = `VerbNotes Recovery Codes\n${'='.repeat(30)}\nSave these codes in a secure place.\nEach code can only be used once.\n\n${recoveryCodes.join('\n')}\n`;
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'verbnotes-recovery-codes.txt';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  if (!status) return null;

  return (
    <Card className="bg-navy-900/40 border-white/5">
      <CardHeader>
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <ShieldCheck className="text-cyan" size={20} />
          Two-Factor Authentication
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-slate-400 text-sm">
          Add an extra layer of security with Google Authenticator. You'll need to enter a code from the app each time you log in.
        </p>

        {/* Recovery codes modal */}
        {recoveryCodes && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 space-y-3" data-testid="recovery-codes-panel">
            <div className="flex items-center gap-2">
              <KeyRound className="text-amber-400" size={18} />
              <span className="text-amber-400 text-sm font-semibold">Save your recovery codes</span>
            </div>
            <p className="text-slate-300 text-xs leading-relaxed">
              If you lose access to your authenticator app, you can use one of these codes to sign in. Each code can only be used once. Store them somewhere safe.
            </p>
            <div className="grid grid-cols-2 gap-2 bg-navy-950 rounded-lg p-3">
              {recoveryCodes.map((c, i) => (
                <code key={i} className="text-cyan text-sm font-mono" data-testid={`recovery-code-${i}`}>{c}</code>
              ))}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleCopyCodes} className="border-white/10 text-slate-300 hover:text-white text-xs gap-1" data-testid="copy-recovery-codes">
                Copy codes
              </Button>
              <Button size="sm" variant="outline" onClick={handleDownloadCodes} className="border-white/10 text-slate-300 hover:text-white text-xs gap-1" data-testid="download-recovery-codes">
                Download .txt
              </Button>
              <Button size="sm" onClick={() => setRecoveryCodes(null)} className="bg-cyan text-navy-950 hover:bg-cyan/90 text-xs ml-auto" data-testid="dismiss-recovery-codes">
                I've saved them
              </Button>
            </div>
          </motion.div>
        )}

        {status.enabled ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
              <ShieldCheck className="text-emerald-400" size={18} />
              <span className="text-emerald-400 text-sm font-medium">2FA is enabled</span>
            </div>
            <div className="flex gap-2">
              <Input
                value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code to disable"
                className="bg-navy-950 border-white/10 text-white max-w-[200px]"
                data-testid="2fa-disable-code"
              />
              <Button variant="destructive" size="sm" onClick={handleDisable} disabled={loading} data-testid="2fa-disable-btn">
                {loading ? <Loader2 className="animate-spin" size={14} /> : 'Disable 2FA'}
              </Button>
            </div>
          </div>
        ) : setupData ? (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div className="bg-white p-2 rounded-xl">
                <img src={setupData.qr_code} alt="QR Code" className="w-36 h-36" data-testid="2fa-qr-code" />
              </div>
              <div className="space-y-2 flex-1">
                <p className="text-slate-300 text-sm">1. Open Google Authenticator</p>
                <p className="text-slate-300 text-sm">2. Scan this QR code</p>
                <p className="text-slate-300 text-sm">3. Enter the 6-digit code below</p>
                <div className="mt-2">
                  <span className="text-slate-500 text-xs">Manual key:</span>
                  <code className="block text-xs text-cyan bg-navy-950 p-2 rounded mt-1 break-all" data-testid="2fa-manual-key">{setupData.secret}</code>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Input
                value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="bg-navy-950 border-white/10 text-white max-w-[200px]"
                data-testid="2fa-verify-code"
              />
              <Button onClick={handleVerify} disabled={loading} className="bg-cyan text-navy-950 hover:bg-cyan/90" data-testid="2fa-verify-btn">
                {loading ? <Loader2 className="animate-spin" size={14} /> : 'Verify & Enable'}
              </Button>
            </div>
          </motion.div>
        ) : (
          <Button onClick={handleSetup} disabled={loading} className="gap-2 bg-cyan text-navy-950 hover:bg-cyan/90" data-testid="2fa-setup-btn">
            {loading ? <Loader2 className="animate-spin" size={14} /> : <><ShieldCheck size={16} /> Set Up 2FA</>}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const [weeklyEnabled, setWeeklyEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const [prefsRes, subRes] = await Promise.all([
          preferencesApi.get(),
          axios.get(`${API}/subscription/me`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setWeeklyEnabled(prefsRes.data.weekly_report_enabled || false);
        setSubscription(subRes.data);
      } catch (err) {
        console.error('Failed to fetch preferences:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPrefs();
  }, [token]);

  const handleToggle = async (checked) => {
    setToggling(true);
    try {
      await preferencesApi.update({ weekly_report_enabled: checked });
      setWeeklyEnabled(checked);
      toast.success(checked ? t('mood_weekly_enabled') : t('mood_weekly_disabled'));
    } catch (err) {
      toast.error('Failed to update preference');
    } finally {
      setToggling(false);
    }
  };

  const handlePreview = async () => {
    setReportLoading(true);
    setReportError(null);
    setReport(null);
    try {
      const res = await moodsApi.getWeeklyReport();
      setReport(res.data);
    } catch (err) {
      setReportError(err.response?.status === 404 ? t('mood_weekly_no_data') : 'Failed to generate report preview.');
    } finally {
      setReportLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="text-cyan animate-pulse">Loading...</div></div>;
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl text-white mb-1">{t('settings_title')}</h1>
        <p className="text-slate-400">{t('settings_preferences')}</p>
      </div>

      {/* Subscription Status */}
      {subscription && (
        <Card className="bg-navy-900/40 border-white/5">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Crown className={subscription.tier === 'free' ? 'text-slate-500' : 'text-gold'} size={22} />
                <div>
                  <p className="text-white font-medium capitalize">{subscription.tier} Plan</p>
                  <p className="text-slate-500 text-xs">
                    {subscription.tier === 'free' ? 'Upgrade for full access' :
                     subscription.expires_at ? `Expires: ${new Date(subscription.expires_at).toLocaleDateString()}` : 'Lifetime access'}
                  </p>
                </div>
              </div>
              <Link to="/dashboard/pricing">
                <Button variant="outline" size="sm" className="border-white/10 text-slate-300 hover:text-white gap-1" data-testid="upgrade-plan-btn">
                  {subscription.tier === 'free' ? 'Upgrade' : 'Manage Plan'} <ArrowRight size={14} />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 2FA */}
      <TwoFactorSection token={token} />

      {/* Weekly Mood Report */}
      <Card className="bg-navy-900/40 border-white/5">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <Mail className="text-cyan" size={20} />
            {t('mood_weekly_title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-slate-400 text-sm">{t('mood_weekly_desc')}</p>
          <div className="flex items-center justify-between p-4 rounded-xl bg-navy-950/60 border border-white/5">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${weeklyEnabled ? 'bg-cyan/15 text-cyan' : 'bg-white/5 text-slate-500'}`}>
                <Mail size={20} />
              </div>
              <div>
                <p className="text-white text-sm font-medium">{weeklyEnabled ? t('mood_weekly_toggle_off') : t('mood_weekly_toggle_on')}</p>
                <p className="text-slate-500 text-xs">{user?.email}</p>
              </div>
            </div>
            <Switch checked={weeklyEnabled} onCheckedChange={handleToggle} disabled={toggling} data-testid="weekly-report-toggle" />
          </div>
          <div className="pt-2">
            <Button variant="outline" onClick={handlePreview} disabled={reportLoading} className="gap-2 border-white/10 text-slate-300 hover:text-white hover:bg-white/5" data-testid="preview-report-btn">
              {reportLoading ? <Loader2 className="animate-spin" size={16} /> : <Eye size={16} />}
              {t('mood_weekly_preview')}
            </Button>
          </div>
          {reportError && (
            <div className="p-4 rounded-xl bg-navy-950/60 border border-white/5"><p className="text-slate-400 text-sm text-center">{reportError}</p></div>
          )}
          {report && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-xl bg-navy-950/60 border border-white/5 space-y-3" data-testid="weekly-report-preview">
              <div className="flex items-center gap-2 mb-2"><Sparkles className="text-gold" size={16} /><span className="text-gold text-sm font-medium">{report.period_start} — {report.period_end}</span></div>
              <div className="flex gap-6 mb-2">
                <div><span className="text-slate-500 text-xs">Avg Mood</span><p className="text-white text-xl font-heading">{report.mood_average}/5</p></div>
                <div><span className="text-slate-500 text-xs">Check-ins</span><p className="text-white text-xl font-heading">{report.total_checkins}</p></div>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">{report.summary}</p>
              {report.highlights?.length > 0 && (
                <div><span className="text-slate-400 text-xs font-medium uppercase tracking-wider">Highlights</span>
                  <ul className="mt-1 space-y-1">{report.highlights.map((h, i) => (<li key={i} className="flex items-start gap-2 text-sm text-slate-300"><span className="text-cyan mt-0.5">&#x2022;</span><span>{h}</span></li>))}</ul>
                </div>
              )}
              <div className="border-t border-white/5 pt-3"><span className="text-slate-400 text-xs font-medium uppercase tracking-wider">Recommendation</span><p className="text-slate-300 text-sm mt-1">{report.recommendation}</p></div>
            </motion.div>
          )}
        </CardContent>
      </Card>

      {/* Connected Services */}
      <ConnectedServicesSection />
    </div>
  );
}

const serviceIcons = { google_drive: HardDrive, gmail: Inbox, dropbox: Cloud };

function ConnectedServicesSection() {
  const { t } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);

  useEffect(() => {
    servicesApi.getStatus().then(r => setServices(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleConnect = async (serviceId) => {
    setConnecting(serviceId);
    try {
      const res = await servicesApi.connect(serviceId);
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to connect';
      toast.error(detail);
    } finally { setConnecting(null); }
  };

  const handleDisconnect = async (serviceId) => {
    try {
      await servicesApi.disconnect(serviceId);
      setServices(prev => prev.map(s => s.id === serviceId ? { ...s, connected: false, connected_at: null } : s));
      toast.success('Service disconnected');
    } catch { toast.error('Failed to disconnect'); }
  };

  if (loading) return null;

  return (
    <Card className="bg-navy-900/40 border-white/5">
      <CardHeader>
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <Link2 className="text-cyan" size={20} />
          {t('settings_connected')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-slate-400 text-sm mb-4">{t('settings_connected_desc')}</p>
        {services.map((svc) => {
          const Icon = serviceIcons[svc.id] || Cloud;
          return (
            <div key={svc.id} className="flex items-center justify-between p-4 rounded-xl bg-navy-950/60 border border-white/5" data-testid={`service-${svc.id}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${svc.connected ? 'bg-emerald-500/15 text-emerald-400' : 'bg-white/5 text-slate-500'}`}>
                  <Icon size={20} />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{svc.name}</p>
                  <p className="text-slate-500 text-xs">{svc.connected ? `Connected ${svc.connected_at ? new Date(svc.connected_at).toLocaleDateString() : ''}` : svc.description}</p>
                </div>
              </div>
              {svc.connected ? (
                <Button variant="outline" size="sm" onClick={() => handleDisconnect(svc.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`disconnect-${svc.id}`}>
                  <Unlink size={14} className="mr-1" />{t('settings_disconnect')}
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => handleConnect(svc.id)} disabled={connecting === svc.id} className="border-white/10 text-slate-300 hover:text-white hover:bg-white/5" data-testid={`connect-${svc.id}`}>
                  {connecting === svc.id ? <Loader2 className="animate-spin" size={14} /> : <><Link2 size={14} className="mr-1" />{t('settings_connect')}</>}
                </Button>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
